// "That was me" (protocol 1.10, feature `speech_mine`): "only listen to me" sometimes takes the
// user's own voice for someone else's. The user marks that speech as theirs from the transcript
// or the guests panel; the kit moves it to them, sends it to the assistant once and, only if it
// sounded close enough to their voice, learns from it. A toast says what happened (with Undo
// for a learning), and the "Marked as me" history shows every mark, the ones not learned
// highlighted with their score against the floor ("0.31, needs 0.40").

import type { MarkInfo, ServerMsg, ServerMsgOf } from "./protocol";
import { injectStyles } from "./styles";

type Reattributed = ServerMsgOf<"speech.reattributed">;

/** Why a mark was not learned, in the UI's words. */
export function notLearnedMessage(reason: string): string {
  switch (reason) {
    case "below_floor":
      return "Not learned: too different from your voice";
    case "two_voices":
      return "Not learned: two voices at once";
    case "no_voice":
      return "Not learned: too short to learn from";
    case "mic_not_enrolled":
      return "Not learned: this mic hasn't learned your voice yet";
    case "store_failed":
      return "Not learned: couldn't save your voice";
    case "undone":
      return "Learning undone";
    case "replaced":
      return "No longer learned: replaced by newer ones";
    case "re_enrolled":
      return "No longer learned: the mic was enrolled again";
    default:
      return "Not learned";
  }
}

/** A score against the learn floor: "0.31, needs 0.40". */
export function scoreAgainstFloor(score: number, floor: number): string {
  return `${score.toFixed(2)}, needs ${floor.toFixed(2)}`;
}

/** The confirmation for one "That was me" (`speech.reattributed`). */
export function mineToast(m: Reattributed): string {
  // Kit 0.12: only the latest thing the user said is sent on its own; older lines are theirs
  // ("Send to assistant" sends them).
  const moved = m.delivered
    ? m.late
      ? "Marked as you and sent to the assistant, noted as said earlier."
      : "Marked as you and sent to the assistant."
    : "Marked as you.";
  // Not sent: say so, so the "Send to assistant" on the line makes sense.
  const unsent = !m.delivered && m.sendable ? " Not sent: it wasn't the last thing you said." : "";
  if (m.learned) return `${moved} Learned.${unsent}`;
  const why = notLearnedMessage(m.reason);
  const learned = m.reason === "below_floor" ? `${why} (${scoreAgainstFloor(m.score, m.floor)}).` : `${why}.`;
  return `${moved} ${learned}${unsent}`;
}

/** The confirmation for "Send to assistant" (`speech.mine.sent`). */
export function sentToast(m: { late?: boolean; before_turn_id?: number }): string {
  // Kit 0.13: sent after a later turn of the user's, the assistant is told it came first.
  if (m.before_turn_id != null) return "Sent to the assistant, noted as said before your last message.";
  return m.late ? "Sent to the assistant, noted as said earlier." : "Sent to the assistant.";
}

/** What "Send to assistant" needs of a client (`VoiceKitClient`). */
export interface SendClient {
  speechMineSend(ignoredId: string): Promise<void>;
  on(type: "speech.mine.sent", fn: (m: ServerMsgOf<"speech.mine.sent">) => void): () => void;
}

/**
 * Where "That was me" and "Send to assistant" say what happened: a toast in the kit's own UI,
 * or wherever a host shows its notes ({@link ignoredLineControls}).
 */
export type MineNotice = (text: string, opts?: ToastOptions) => void;

/** A refusal the kit sent (`ClientError`, with its `code`). */
type Refusal = Error & { code?: string };

/**
 * Sends a marked line that was not sent on its own to the assistant (`speech.mine.send`), and
 * says so through `notice` once the kit confirms (`speech.mine.sent`, passed to `confirmed`).
 * Rejects (after an error notice) if the kit refused; `explain` may word a refusal itself. The
 * one implementation behind {@link sendToAssistant} and {@link ignoredLineControls}.
 */
export function sendMine(
  client: SendClient,
  ignoredId: string,
  notice: MineNotice,
  opts: { timeoutMs?: number; explain?: (e: Refusal) => string | undefined; confirmed?: (m: ServerMsgOf<"speech.mine.sent">) => void } = {},
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const off = client.on("speech.mine.sent", (m) => {
    if (m.ignored_id !== ignoredId) return;
    off();
    clearTimeout(timer);
    notice(sentToast(m));
    opts.confirmed?.(m);
  });
  return client.speechMineSend(ignoredId).then(
    () => {
      timer = setTimeout(() => off(), opts.timeoutMs ?? MINE_CONFIRM_MS);
    },
    (e: Refusal) => {
      off();
      // Sent already (another window, or a double tap): what the user wanted happened.
      if (e.code === "already_sent") return;
      const gone = e.code === "unknown_speech" ? "That line can no longer be sent (it was too long ago)." : undefined;
      notice(opts.explain?.(e) ?? gone ?? `Couldn't send it: ${e.message}`, { error: true });
      throw e;
    },
  );
}

/**
 * Kit 0.12: sends a marked line that was not sent on its own to the assistant, and confirms it
 * with a toast in `host` ("Sent to the assistant"). Rejects (after an error toast) if the kit
 * refused; `explain` may word a refusal itself.
 */
export function sendToAssistant(
  client: SendClient,
  host: HTMLElement,
  ignoredId: string,
  timeoutMs = MINE_CONFIRM_MS,
  explain?: (e: Error & { code?: string }) => string | undefined,
): Promise<void> {
  return sendMine(client, ignoredId, (text, o) => void showToast(host, text, o), { timeoutMs, explain });
}

export interface ToastOptions {
  /** A button on the toast (e.g. Undo). */
  action?: { label: string; run: () => unknown };
  error?: boolean;
  /** Shown this long. Default 6 s. */
  ms?: number;
}

/** A short message at the bottom of `host` (replaces the one showing). Returns its element. */
export function showToast(host: HTMLElement, text: string, opts: ToastOptions = {}): HTMLElement {
  injectStyles();
  host.querySelector(":scope > .vk-toast")?.remove();
  const el = document.createElement("div");
  el.className = opts.error ? "vk-toast error" : "vk-toast";
  el.setAttribute("role", opts.error ? "alert" : "status");
  const p = document.createElement("span");
  p.textContent = text;
  el.append(p);
  if (opts.action) {
    const { label, run } = opts.action;
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.addEventListener("click", () => {
      el.remove();
      run();
    });
    el.append(b);
  }
  host.append(el);
  setTimeout(() => el.remove(), opts.ms ?? 6000);
  return el;
}

/** What "That was me" needs of a client (`VoiceKitClient`). */
export interface MineClient {
  speechMine(ignoredId: string): Promise<void>;
  speechUnlearn(markId: string): Promise<void>;
  on(type: "speech.reattributed", fn: (m: Reattributed) => void): () => void;
}

/**
 * Marks `ignoredId` as the user's (`speech.mine`) and says what happened through `notice` once
 * the kit confirms (`speech.reattributed`, passed to `confirmed`): what moved, whether it was
 * learned (with Undo) or why not ("Not learned: too different from your voice"). Rejects (after
 * an error notice) if the kit refused; `explain` may word a refusal itself (else "Couldn't mark
 * it as yours: ..."). The one implementation behind {@link markAsMine} and
 * {@link ignoredLineControls}.
 */
export function markMine(
  client: MineClient,
  ignoredId: string,
  notice: MineNotice,
  opts: { timeoutMs?: number; explain?: (e: Refusal) => string | undefined; confirmed?: (m: Reattributed) => void } = {},
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const off = client.on("speech.reattributed", (m) => {
    if (m.ignored_id !== ignoredId) return;
    off();
    clearTimeout(timer);
    const undo = { label: "Undo learning", run: () => client.speechUnlearn(m.mark_id).catch(() => {}) };
    notice(mineToast(m), m.learned ? { action: undo } : {});
    opts.confirmed?.(m);
  });
  return client.speechMine(ignoredId).then(
    () => {
      // The kit took it; its confirmation follows once the voiceprint is stored. Not forever.
      timer = setTimeout(() => {
        off();
        notice("Marked as yours, but the voice kit never confirmed it. Check Marked as me.", { error: true });
      }, opts.timeoutMs ?? MINE_CONFIRM_MS);
    },
    (e: Refusal) => {
      off();
      notice(opts.explain?.(e) ?? `Couldn't mark it as yours: ${e.message}`, { error: true });
      throw e;
    },
  );
}

/**
 * Marks `ignoredId` as the user's and confirms it with a toast in `host` ({@link markMine}).
 */
export function markAsMine(
  client: MineClient,
  host: HTMLElement,
  ignoredId: string,
  timeoutMs = MINE_CONFIRM_MS,
  explain?: (e: Error & { code?: string }) => string | undefined,
): Promise<void> {
  return markMine(client, ignoredId, (text, o) => void showToast(host, text, o), { timeoutMs, explain });
}

/** How long {@link markAsMine} waits for the kit's `speech.reattributed`. */
export const MINE_CONFIRM_MS = 10_000;

/** The "That was me" history (`speech.marks`), for the "Marked as me" list. */
export class MarksModel {
  /** Oldest first. */
  marks: MarkInfo[] = [];
  /** The kit offers "That was me" (it said so in `welcome`, or sent a mark). */
  active = false;
  private readonly listeners: Array<() => void> = [];

  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  /** Feed every kit event here. */
  apply(m: ServerMsg): void {
    if (m.type === "welcome" && m.features?.includes("speech_mine") && !this.active) {
      this.active = true;
      this.changed();
    } else if (m.type === "speech.marks") {
      this.active = true;
      this.marks = m.marks;
      this.changed();
    }
  }

  /** Marks that were not learned (not counting ones the user undid). */
  get notLearned(): MarkInfo[] {
    return this.marks.filter((m) => !m.learned && m.reason !== "undone");
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }
}

/** The count badge: "3 not learned" (empty with none). */
export function marksBadge(n: number): string {
  return n ? `${n} not learned` : "";
}

export interface MarksActions {
  /** Undo the learning of a mark (`client.speechUnlearn`). */
  unlearn(markId: string): Promise<void>;
}

export interface MarksView {
  destroy(): void;
}

/**
 * Mount the "Marked as me" history into `root`: newest first, the ones not learned highlighted
 * with their score against the floor, Undo on the learned ones, and a "3 not learned" badge.
 * Hidden while there is nothing to show.
 */
export function mountMarks(root: HTMLElement, model: MarksModel, actions: MarksActions): MarksView {
  injectStyles();
  root.classList.add("vk-marks");
  const details = document.createElement("details");
  const summary = document.createElement("summary");
  const title = document.createElement("span");
  title.textContent = "Marked as me";
  const badge = document.createElement("span");
  badge.className = "vk-marks-badge";
  summary.append(title, badge);
  const note = document.createElement("p");
  note.className = "vk-marks-note";
  note.textContent = "Speech you said was yours. It's learned into your voice only if it sounded close enough.";
  const list = document.createElement("ol");
  list.className = "vk-marks-list";
  details.append(summary, note, list);
  root.replaceChildren(details);

  const item = (m: MarkInfo): HTMLLIElement => {
    const li = document.createElement("li");
    li.className = "vk-mark";
    li.dataset.mark = m.mark_id;
    const notLearned = !m.learned && m.reason !== "undone";
    li.classList.toggle("not-learned", notLearned);
    const time = document.createElement("time");
    const at = new Date(m.at * 1000);
    time.dateTime = at.toISOString();
    time.textContent = at.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
    const words = document.createElement("span");
    words.className = "vk-mark-words";
    words.textContent = m.text ?? "Your words (not kept)";
    const status = document.createElement("span");
    status.className = "vk-mark-status";
    const mic = m.source ? ` \u00b7 ${m.source === "pc" ? "this PC's mic" : "phone mic"}` : "";
    if (m.learned) status.textContent = `Learned into your voice${mic}`;
    else if (m.reason === "below_floor") status.textContent = `${notLearnedMessage(m.reason)} \u00b7 ${scoreAgainstFloor(m.score, m.floor)}`;
    else status.textContent = notLearnedMessage(m.reason);
    if (m.guest) status.title = `Had been put with ${m.guest}${m.guest_score != null ? ` (${m.guest_score.toFixed(2)})` : ""}`;
    li.append(time, words, status);
    if (m.learned) {
      const undo = document.createElement("button");
      undo.type = "button";
      undo.textContent = "Undo";
      undo.title = "Forget what was learned from this";
      undo.addEventListener("click", () => {
        undo.disabled = true;
        actions.unlearn(m.mark_id).catch(() => (undo.disabled = false));
      });
      li.append(undo);
    }
    return li;
  };

  const render = () => {
    root.hidden = model.marks.length === 0;
    const n = model.notLearned.length;
    badge.textContent = marksBadge(n);
    badge.hidden = n === 0;
    list.replaceChildren(...[...model.marks].reverse().map(item));
  };
  const off = model.onChange(render);
  render();
  return {
    destroy() {
      off();
      root.replaceChildren();
      root.classList.remove("vk-marks");
    },
  };
}

/**
 * Plays clips of ignored speech (`client.speechClip`) through the local speaker, one at a time:
 * starting one stops the other. A clip the kit no longer keeps is remembered as expired. While a
 * clip plays the kit hears its mic as silence (it mutes it for the clip's length when it serves
 * the clip), so the playback never becomes a turn or a barge-in.
 */
export class ClipPlayer {
  /** The clip playing (or loading). */
  playing: string | null = null;
  /** Clips the kit no longer keeps. */
  readonly expired = new Set<string>();
  private own: AudioContext | null = null;
  private source: AudioBufferSourceNode | null = null;
  /** The clip the kit was told is playing (`speech.clip.playing`). */
  private announced: string | null = null;
  private readonly listeners: Array<() => void> = [];
  private readonly output: () => ClipOutput;
  private readonly onPlaying: (ignoredId: string, playing: boolean) => void;
  private readonly onError: (ignoredId: string, e: Error & { code?: string }) => void;

  /**
   * `fetch`: the clip (`client.speechClip`). `output`: where it plays, taken inside the tap
   * before anything is awaited (iOS starts audio only from a gesture): best the page player's
   * own (`WebAudioPlayer.clipOutput`), so no second context disturbs the mic's; by default a
   * context of its own, made in the tap. `onPlaying`: tell the kit it started or stopped
   * (`client.speechClipPlaying`), so the mic is heard as silence just while it plays.
   * `onError`: the kit refused the clip (other than expired), e.g. to say why.
   */
  constructor(
    private readonly fetch: (ignoredId: string) => Promise<Int16Array>,
    opts: {
      output?: () => ClipOutput;
      onPlaying?: (ignoredId: string, playing: boolean) => void;
      onError?: (ignoredId: string, e: Error & { code?: string }) => void;
    } = {},
  ) {
    this.output =
      opts.output ??
      (() => {
        const ctx = (this.own ??= new AudioContext());
        return { ctx, destination: ctx.destination };
      });
    this.onPlaying = opts.onPlaying ?? (() => {});
    this.onError = opts.onError ?? (() => {});
  }

  onChange(fn: () => void): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  /** Play `ignoredId`, or stop it if it is the one playing. */
  async toggle(ignoredId: string): Promise<void> {
    if (this.playing === ignoredId) return this.stop();
    this.stop();
    // In the tap, before any await: the output is made (or woken) now, while iOS allows it.
    const out = this.output();
    void out.ctx.resume?.();
    this.playing = ignoredId;
    this.changed();
    let pcm: Int16Array;
    try {
      pcm = await this.fetch(ignoredId);
    } catch (e) {
      if ((e as { code?: string }).code === "clip_expired") this.expired.add(ignoredId);
      else this.onError(ignoredId, e as Error);
      if (this.playing === ignoredId) this.playing = null;
      this.changed();
      return;
    }
    if (this.playing !== ignoredId) return; // stopped (or another started) while it loaded
    const ctx = out.ctx;
    const buf = ctx.createBuffer(1, Math.max(1, pcm.length), 16_000);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < pcm.length; i++) ch[i] = pcm[i] / 32768;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(out.destination);
    src.onended = () => {
      if (this.source !== src) return;
      this.source = null;
      this.playing = null;
      this.announce(null);
      this.changed();
    };
    this.source = src;
    src.start();
    this.announce(ignoredId);
  }

  /** Tells the kit which clip plays now (`null`: none). */
  private announce(id: string | null): void {
    if (this.announced === id) return;
    if (this.announced !== null) this.onPlaying(this.announced, false);
    this.announced = id;
    if (id !== null) this.onPlaying(id, true);
  }

  stop(): void {
    const src = this.source;
    this.source = null;
    if (src) {
      src.onended = null;
      try {
        src.stop();
      } catch {
        /* not started */
      }
    }
    this.announce(null);
    if (this.playing !== null) {
      this.playing = null;
      this.changed();
    }
  }

  private changed(): void {
    for (const fn of this.listeners) fn();
  }
}

/** Where a clip plays: a running context and the node to connect to. */
export interface ClipOutput {
  ctx: AudioContext;
  destination: AudioNode;
}

/** The play/stop button for the clip of `ignoredId`, kept in step with `player`. */
export function clipButton(ignoredId: string, player: ClipPlayer): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = "vk-clip";
  b.dataset.clip = ignoredId;
  const sync = () => {
    const expired = player.expired.has(ignoredId);
    const playing = player.playing === ignoredId;
    b.disabled = expired;
    b.textContent = expired ? "Clip expired" : playing ? "Stop" : "Play";
    b.setAttribute("aria-label", expired ? "Clip expired" : playing ? "Stop the clip" : "Play the clip");
    b.title = expired ? "The kit keeps clips 10 minutes, and not after the app closes" : "Hear what was said";
    b.classList.toggle("playing", playing);
  };
  sync();
  const off = player.onChange(() => {
    // A button no longer on the page (its line was redrawn) stops listening.
    if (!b.isConnected && b.dataset.mounted) return off();
    if (b.isConnected) b.dataset.mounted = "1";
    sync();
  });
  b.addEventListener("click", (e) => {
    e.stopPropagation();
    void player.toggle(ignoredId);
  });
  return b;
}
